import { useCallback, useEffect } from "react"

import { useForm } from "react-hook-form"

import styles from "./CommissionRateForm.module.scss"

import {
	commissionRateResolver,
	type CommissionRateFormData,
} from "@/souhl_admin/utils/forms/commissionRate"

type Props = {
	defaultValues: CommissionRateFormData
	onSubmit: (data: CommissionRateFormData) => Promise<void>
	isSubmitting: boolean
}

const CommissionRateForm = ({
	defaultValues,
	onSubmit,
	isSubmitting,
}: Props) => {
	const {
		register,
		handleSubmit,
		reset,
		watch,
		setValue,
		getValues,
		formState: { errors, isDirty },
	} = useForm<CommissionRateFormData>({
		resolver: commissionRateResolver,
		defaultValues,
	})

	useEffect(() => {
		reset(defaultValues)
	}, [defaultValues, reset])

	const isSplit = watch("isSplitNewCustomerRate")

	const onFormSubmit = useCallback(
		async (data: CommissionRateFormData) => {
			// 分離 OFF のときは新規客用に既存客用と同じ値を入れて BE に送る
			const payload: CommissionRateFormData = {
				...data,
				newCustomerRereservationCommissionRate: data.isSplitNewCustomerRate
					? data.newCustomerRereservationCommissionRate
					: data.existingCustomerRereservationCommissionRate,
			}
			await onSubmit(payload)
			reset(payload)
		},
		[onSubmit, reset]
	)

	const handleCancel = useCallback(() => {
		reset(defaultValues)
	}, [defaultValues, reset])

	const handleToggleSplit = useCallback(
		(checked: boolean) => {
			setValue("isSplitNewCustomerRate", checked, { shouldDirty: true })
			if (checked) {
				// 分離を ON にしたら新規客用の初期値を既存客用と同値にしておく
				setValue(
					"newCustomerRereservationCommissionRate",
					getValues("existingCustomerRereservationCommissionRate"),
					{ shouldDirty: true }
				)
			}
		},
		[setValue, getValues]
	)

	return (
		<form
			className={styles.form}
			onSubmit={handleSubmit(onFormSubmit)}
			noValidate
		>
			<div className={styles.form_card}>
				<div className={styles.field}>
					<label className={styles.label}>
						キャンセル料回収手数料率（税抜）
						<span className={styles.required}>*</span>
					</label>
					<div className={styles.input_row}>
						<input
							type="number"
							className={`${styles.input} ${errors.cancelFeeCommissionRate ? styles.input_error : ""}`}
							disabled={isSubmitting}
							{...register("cancelFeeCommissionRate", {
								valueAsNumber: true,
							})}
						/>
						<span className={styles.unit}>%</span>
					</div>
					{errors.cancelFeeCommissionRate && (
						<span className={styles.error_message}>
							{errors.cancelFeeCommissionRate.message}
						</span>
					)}
				</div>

				<label className={styles.toggle}>
					<input
						type="checkbox"
						checked={!!isSplit}
						disabled={isSubmitting}
						onChange={(e) => handleToggleSplit(e.target.checked)}
					/>
					新規顧客に別の再予約手数料率を設定する
				</label>

				<div className={styles.rate_row}>
					<div className={styles.field}>
						<label className={styles.label}>
							{isSplit
								? "再予約手数料率・既存顧客用（税抜）"
								: "再予約手数料率（税抜）"}
							<span className={styles.required}>*</span>
						</label>
						<div className={styles.input_row}>
							<input
								type="number"
								className={`${styles.input} ${errors.existingCustomerRereservationCommissionRate ? styles.input_error : ""}`}
								disabled={isSubmitting}
								{...register("existingCustomerRereservationCommissionRate", {
									valueAsNumber: true,
								})}
							/>
							<span className={styles.unit}>%</span>
						</div>
						{errors.existingCustomerRereservationCommissionRate && (
							<span className={styles.error_message}>
								{errors.existingCustomerRereservationCommissionRate.message}
							</span>
						)}
					</div>

					{isSplit && (
						<div className={styles.field}>
							<label className={styles.label}>
								再予約手数料率・新規顧客用（税抜）
								<span className={styles.required}>*</span>
							</label>
							<div className={styles.input_row}>
								<input
									type="number"
									className={`${styles.input} ${errors.newCustomerRereservationCommissionRate ? styles.input_error : ""}`}
									disabled={isSubmitting}
									{...register("newCustomerRereservationCommissionRate", {
										valueAsNumber: true,
									})}
								/>
								<span className={styles.unit}>%</span>
							</div>
							{errors.newCustomerRereservationCommissionRate && (
								<span className={styles.error_message}>
									{errors.newCustomerRereservationCommissionRate.message}
								</span>
							)}
						</div>
					)}
				</div>
			</div>

			<div className={styles.buttons}>
				<button
					type="button"
					className={styles.cancel_button}
					onClick={handleCancel}
					disabled={!isDirty || isSubmitting}
				>
					キャンセル
				</button>
				<button
					type="submit"
					className={styles.submit_button}
					disabled={!isDirty || isSubmitting}
				>
					{isSubmitting ? "保存中..." : "保存"}
				</button>
			</div>
		</form>
	)
}

export default CommissionRateForm
